import React, { useState, useMemo, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Plus, Trash2, Loader2, AlertCircle } from "lucide-react";
import Seo from "@/components/Seo";
import { Eyebrow } from "@/components/ui-bits";
import { publicApi, formatApiError } from "@/lib/api";
import { PRODUCTS, INVERTER_PRODUCTS, PANEL_PRODUCTS, COMPANY } from "@/data/site";
import { useCart } from "@/context/CartContext";
import { publicApi as catalogueApi } from "@/lib/api";

const FULFILMENT = ["Collection", "Delivery"];
const AU_STATES = ["ACT", "NSW", "NT", "QLD", "SA", "TAS", "VIC", "WA"];

/** Australian postcode to state, matching the backend carve-outs for ACT and NT. */
function stateForPostcode(postcode) {
  if (!/^\d{4}$/.test(postcode)) return "";
  const n = Number(postcode);
  if ((n >= 200 && n <= 299) || (n >= 2600 && n <= 2618) || (n >= 2900 && n <= 2920)) return "ACT";
  if (n >= 800 && n <= 899) return "NT";
  return { 1: "NSW", 2: "NSW", 3: "VIC", 4: "QLD", 5: "SA", 6: "WA", 7: "TAS", 8: "VIC", 9: "QLD" }[postcode[0]] || "";
}

const emptyForm = {
  business_name: "",
  trading_name: "",
  abn: "",
  contact_name: "",
  mobile: "",
  email: "",
  postcode: "",
  street: "",
  suburb: "",
  state: "",
  residential: false,
  fulfilment_preference: "Collection",
  timeframe: "",
  notes: "",
  contractor_declaration: false,
  consent: false,
  marketing_consent: false,
  company_website: "", // honeypot
};

function Field({ label, required, error, children, htmlFor, hint }) {
  return (
    <div>
      <label htmlFor={htmlFor} className="block font-display text-sm font-semibold text-ei-navy">
        {label} {required && <span className="text-ei-golddark">*</span>}
      </label>
      {hint && <p className="text-xs text-ei-graphite/60 mt-0.5">{hint}</p>}
      <div className="mt-1.5">{children}</div>
      {error && (
        <p className="mt-1 text-xs text-red-700 flex items-center gap-1" role="alert">
          <AlertCircle className="w-3.5 h-3.5" /> {error}
        </p>
      )}
    </div>
  );
}

const inputCls =
  "w-full min-h-[44px] rounded-sm border border-ei-mist bg-white px-3.5 py-2.5 text-ei-graphite placeholder:text-ei-graphite/40 focus:border-ei-navy outline-none transition-colors";

export default function RequestQuote() {
  const [params] = useSearchParams();
  const cart = useCart();
  const preselect = params.get("product");
  const [catalogue, setCatalogue] = useState([...PRODUCTS.filter((p) => !p.draft), ...INVERTER_PRODUCTS, ...PANEL_PRODUCTS]);
  useEffect(() => {
    catalogueApi.get('/sales/catalogue').then(({ data }) => {
      setCatalogue(data);
      let selection = [];
      try { selection = JSON.parse(params.get('selection') || '[]'); } catch { selection = []; }
      if (params.get('variant')) selection = [[params.get('variant'), params.get('quantity') || 1]];
      if (Array.isArray(selection) && selection.length) {
        const selected = selection.map(([variant, qty]) => ({ product: data.find((p) => p.variant_id === String(variant)), qty: Number(qty) })).filter((s) => s.product && Number.isInteger(s.qty) && s.qty > 0 && s.qty <= 100000);
        if (selected.length) setLines(selected.map((s) => ({ uiId: crypto.randomUUID(), name: s.product.name, quantity: s.qty })));
      }
    }).catch(() => {});
  }, [params]);

  const initialLines = useMemo(() => {
    const found = [...PRODUCTS, ...INVERTER_PRODUCTS, ...PANEL_PRODUCTS].find((p) => p.name === preselect);
    return [{ uiId: crypto.randomUUID(), name: found ? found.name : PRODUCTS.find((p) => !p.draft && !p.component).name, quantity: 1 }];
  }, [preselect]);

  const [form, setForm] = useState(emptyForm);
  const [lines, setLines] = useState(initialLines);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  // Whole cart as one quote request: catalogue items become quote lines, kits and
  // quote-only lines that are not selectable products are written into the notes.
  const fromCart = params.get("cart") === "1";
  useEffect(() => {
    if (!fromCart || !cart?.lines?.length) return;
    const matched = cart.lines.filter((line) => catalogue.some((p) => p.name === line.name));
    const extras = cart.lines.filter((line) => !catalogue.some((p) => p.name === line.name));
    if (matched.length) setLines(matched.map((line) => ({ uiId: crypto.randomUUID(), name: line.name, quantity: line.quantity })));
    if (extras.length) {
      const note = "From my cart: " + extras.map((line) => `${line.name} × ${line.quantity}`).join("; ") + ".";
      setForm((f) => (f.notes?.includes("From my cart:") ? f : { ...f, notes: f.notes ? `${f.notes}\n${note}` : note }));
    }
  }, [fromCart, cart, catalogue]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const setLine = (idx, key, value) =>
    setLines((ls) => ls.map((l, i) => (i === idx ? { ...l, [key]: value } : l)));
  const addLine = () => {
    const remaining = catalogue.map((p) => p.name).filter((n) => !lines.some((l) => l.name === n));
    if (remaining.length === 0) return;
    setLines((ls) => [...ls, { uiId: crypto.randomUUID(), name: remaining[0], quantity: 1 }]);
  };
  const removeLine = (idx) => setLines((ls) => (ls.length > 1 ? ls.filter((_, i) => i !== idx) : ls));

  const validate = () => {
    const e = {};
    if (form.business_name.trim().length < 2) e.business_name = "Business name is required";
    const abnDigits = form.abn.replace(/\D/g, "");
    if (abnDigits.length !== 11) e.abn = "Enter an 11-digit ABN";
    if (form.contact_name.trim().length < 2) e.contact_name = "Contact name is required";
    if (form.mobile.trim().length < 6) e.mobile = "Enter a valid mobile number";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email address";
    if (form.postcode.trim().length < 3) e.postcode = "Enter a postcode";
    if (form.fulfilment_preference === "Delivery") {
      if (form.street.trim().length < 3) e.street = "Enter the delivery street address";
      if (form.suburb.trim().length < 2) e.suburb = "Enter the delivery suburb or city";
      if (!AU_STATES.includes(form.state)) e.state = "Select the state";
      if (!/^\d{4}$/.test(form.postcode.trim())) e.postcode = "Enter a 4-digit postcode for delivery";
    }
    if (lines.some((l) => !l.quantity || l.quantity < 1)) e.products = "Quantities must be at least 1";
    if (!form.contractor_declaration) e.contractor_declaration = "Please confirm your trade eligibility";
    if (!form.consent) e.consent = "Please acknowledge the enquiry consent";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Please correct the highlighted fields");
      return;
    }
    setSubmitting(true);
    try {
      const { street, suburb, state, residential, ...rest } = form;
      const payload = {
        ...rest,
        abn: form.abn.replace(/\D/g, ""),
        delivery_address: street.trim() && suburb.trim() && AU_STATES.includes(state) && /^\d{4}$/.test(form.postcode.trim())
          ? { street: street.trim(), suburb: suburb.trim(), state, postcode: form.postcode.trim(), residential }
          : null,
        products: lines.map((l) => ({ product_id: catalogue.find((p) => p.name === l.name)?.product_id || catalogue.find((p) => p.name === l.name)?.slug, name: l.name, quantity: Number(l.quantity) })),
        source_page: "/request-a-quote",
      };
      const { data } = await publicApi.post("/enquiries", payload);
      setSuccess(data.reference);
      toast.success("Enquiry received");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiError(err, "We couldn't submit your enquiry. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-20 text-center">
        <Seo title="Enquiry received — EI Battery Solutions" />
        <CheckCircle2 className="w-16 h-16 text-ei-golddark mx-auto" strokeWidth={1.5} />
        <h1 className="mt-6 text-3xl font-bold text-ei-navy">Enquiry received</h1>
        <p className="mt-4 text-ei-graphite/90 leading-relaxed">
          Thank you. Your enquiry has been saved and passed to our sales team. Please keep your reference for any
          follow-up.
        </p>
        <div className="mt-6 inline-block bg-white border border-ei-mist rounded-md px-8 py-5">
          <div className="ei-eyebrow">Your reference</div>
          <div className="mt-2 font-display text-2xl font-bold text-ei-navy tnum" data-testid="quote-reference">{success}</div>
        </div>
        <p className="mt-6 text-sm text-ei-graphite/70">
          Our team will review trade eligibility, confirm equipment and prepare your wholesale quote.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-4">
          <Link to="/" className="font-display text-sm font-bold bg-ei-navy text-ei-white px-6 py-3.5 rounded-sm hover:bg-ei-ink min-h-[44px] inline-flex items-center" data-testid="success-home">Back to home</Link>
          <Link to="/products" className="font-display text-sm font-bold text-ei-navy border border-ei-navy/25 px-6 py-3.5 rounded-sm hover:border-ei-navy min-h-[44px] inline-flex items-center">Browse products</Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <Seo title="Request a trade quote — EI Battery Solutions" description="Registered Australian electrical contractors, builders and property developers can request a wholesale quote for batteries and hybrid inverters." />
      <section className="bg-ei-navy text-ei-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-16">
          <Eyebrow className="text-ei-gold">Trade customers only</Eyebrow>
          <h1 className="mt-4 text-4xl sm:text-5xl font-bold tracking-tight">Request a wholesale quote</h1>
          <p className="mt-4 text-ei-white/75 max-w-2xl leading-relaxed">
            Complete your trade details below. Eligibility is checked by our sales team. Prefer to talk? Call{" "}
            <a href={COMPANY.phoneHref} className="text-ei-gold font-semibold">{COMPANY.phone}</a>.
          </p>
        </div>
      </section>

      <form onSubmit={submit} noValidate className="max-w-3xl mx-auto px-4 sm:px-6 py-12 lg:py-16" data-testid="quote-form">
        {/* Honeypot — off-screen and deliberately unlabelled so browser autofill never fills it */}
        <div aria-hidden="true" className="absolute -left-[9999px]" style={{ position: "absolute", left: "-9999px" }}>
          <input type="text" name="ei-no-fill-check" id="ei-no-fill-check" tabIndex={-1} autoComplete="off" value={form.company_website} onChange={(e) => set("company_website", e.target.value)} />
        </div>

        <fieldset className="space-y-6">
          <legend className="font-display text-lg font-semibold text-ei-navy">Your business</legend>
          <div className="grid sm:grid-cols-2 gap-6">
            <Field label="Business name" required error={errors.business_name} htmlFor="business_name">
              <input id="business_name" className={inputCls} value={form.business_name} onChange={(e) => set("business_name", e.target.value)} data-testid="input-business-name" />
            </Field>
            <Field label="Trading name" htmlFor="trading_name" hint="If different from business name">
              <input id="trading_name" className={inputCls} value={form.trading_name} onChange={(e) => set("trading_name", e.target.value)} data-testid="input-trading-name" />
            </Field>
            <Field label="ABN" required error={errors.abn} htmlFor="abn" hint="11 digits">
              <input id="abn" inputMode="numeric" className={inputCls} value={form.abn} onChange={(e) => set("abn", e.target.value)} data-testid="input-abn" />
            </Field>
            <Field label="Postcode" required error={errors.postcode} htmlFor="postcode">
              <input id="postcode" className={inputCls} value={form.postcode} onChange={(e) => { const postcode = e.target.value.replace(/[^0-9]/g, "").slice(0, 4); setForm((f) => ({ ...f, postcode, state: f.state || stateForPostcode(postcode) })); }} data-testid="input-postcode" />
            </Field>
          </div>
        </fieldset>

        <fieldset className="space-y-6 mt-10">
          <legend className="font-display text-lg font-semibold text-ei-navy">Contact person</legend>
          <div className="grid sm:grid-cols-2 gap-6">
            <Field label="Contact name" required error={errors.contact_name} htmlFor="contact_name">
              <input id="contact_name" className={inputCls} value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} data-testid="input-contact-name" />
            </Field>
            <Field label="Mobile" required error={errors.mobile} htmlFor="mobile">
              <input id="mobile" inputMode="tel" className={inputCls} value={form.mobile} onChange={(e) => set("mobile", e.target.value)} data-testid="input-mobile" />
            </Field>
            <Field label="Email" required error={errors.email} htmlFor="email">
              <input id="email" type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="input-email" />
            </Field>
          </div>
        </fieldset>

        <fieldset className="space-y-4 mt-10">
          <legend className="font-display text-lg font-semibold text-ei-navy">Equipment & quantities</legend>
          {errors.products && <p className="text-xs text-red-700">{errors.products}</p>}
          <div className="space-y-3">
            {lines.map((line, idx) => (
              <div key={line.uiId} className="flex gap-3 items-end bg-white border border-ei-mist rounded-md p-4" data-testid={`quote-line-${idx}`}>
                <div className="flex-1">
                  <label className="block text-xs font-semibold text-ei-navy mb-1">Product</label>
                  <select className={inputCls} value={line.name} onChange={(e) => setLine(idx, "name", e.target.value)} data-testid={`select-product-${idx}`}>
                    {catalogue.map((p) => (
                      <option key={p.product_id || p.slug} value={p.name}>{p.name}</option>
                    ))}
                  </select>
                </div>
                <div className="w-28">
                  <label className="block text-xs font-semibold text-ei-navy mb-1">Quantity</label>
                  <input type="number" min={1} className={`${inputCls} tnum`} value={line.quantity} onChange={(e) => setLine(idx, "quantity", e.target.value)} data-testid={`input-quantity-${idx}`} />
                </div>
                <button type="button" onClick={() => removeLine(idx)} disabled={lines.length === 1} className="min-h-[44px] w-11 flex items-center justify-center text-ei-graphite/60 hover:text-red-700 disabled:opacity-30" aria-label="Remove product" data-testid={`remove-line-${idx}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          {lines.length < catalogue.length && (
            <button type="button" onClick={addLine} className="inline-flex items-center gap-2 text-sm font-semibold text-ei-navy hover:text-ei-golddark" data-testid="add-product-line">
              <Plus className="w-4 h-4" /> Add another product
            </button>
          )}
        </fieldset>

        <fieldset className="space-y-6 mt-10">
          <legend className="font-display text-lg font-semibold text-ei-navy">Delivery & notes</legend>
          <div className="grid sm:grid-cols-2 gap-6">
            <Field label="Collection or delivery" required htmlFor="fulfilment">
              <select id="fulfilment" className={inputCls} value={form.fulfilment_preference} onChange={(e) => set("fulfilment_preference", e.target.value)} data-testid="select-fulfilment">
                {FULFILMENT.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </Field>
            <Field label="Target timeframe" htmlFor="timeframe" hint="Optional">
              <input id="timeframe" className={inputCls} value={form.timeframe} onChange={(e) => set("timeframe", e.target.value)} placeholder="e.g. within 4 weeks" data-testid="input-timeframe" />
            </Field>
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="sm:col-span-2">
              <Field label="Delivery street address" required={form.fulfilment_preference === "Delivery"} error={errors.street} htmlFor="street" hint="Where the pallet is delivered — used to price freight accurately">
                <input id="street" className={inputCls} value={form.street} onChange={(e) => set("street", e.target.value)} placeholder="e.g. Unit 3, 24 Fitzgerald Road" data-testid="input-street" />
              </Field>
            </div>
            <Field label="Suburb / city" required={form.fulfilment_preference === "Delivery"} error={errors.suburb} htmlFor="suburb">
              <input id="suburb" className={inputCls} value={form.suburb} onChange={(e) => set("suburb", e.target.value)} data-testid="input-suburb" />
            </Field>
            <Field label="State" required={form.fulfilment_preference === "Delivery"} error={errors.state} htmlFor="state">
              <select id="state" className={inputCls} value={form.state} onChange={(e) => set("state", e.target.value)} data-testid="select-state">
                <option value="">Select state</option>
                {AU_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <label className="flex items-center gap-2 text-sm text-ei-graphite/80 sm:col-span-2" data-testid="check-residential">
              <input type="checkbox" className="w-4 h-4 accent-[#061A33]" checked={form.residential} onChange={(e) => set("residential", e.target.checked)} />
              Residential / home delivery address
            </label>
          </div>
          <Field label="Project notes" htmlFor="notes" hint="Optional">
            <textarea id="notes" rows={4} className={inputCls} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="input-notes" />
          </Field>
        </fieldset>

        <fieldset className="space-y-4 mt-10">
          <label className="flex items-start gap-3 cursor-pointer" data-testid="check-contractor">
            <input type="checkbox" className="mt-1 w-5 h-5 accent-[#061A33]" checked={form.contractor_declaration} onChange={(e) => set("contractor_declaration", e.target.checked)} />
            <span className="text-sm text-ei-graphite/90">
              I confirm I am, or represent, a registered electrical contractor holding the required solar and battery
              installation licences, or a builder or property developer who will engage an appropriately licensed
              electrical contractor and accredited installer for this work. <span className="text-ei-golddark">*</span>
            </span>
          </label>
          {errors.contractor_declaration && <p className="text-xs text-red-700 -mt-2 ml-8">{errors.contractor_declaration}</p>}

          <label className="flex items-start gap-3 cursor-pointer" data-testid="check-consent">
            <input type="checkbox" className="mt-1 w-5 h-5 accent-[#061A33]" checked={form.consent} onChange={(e) => set("consent", e.target.checked)} />
            <span className="text-sm text-ei-graphite/90">
              I consent to EI Battery Solutions contacting me about this enquiry and handling my details per the{" "}
              <Link to="/policies/privacy" className="text-ei-navy underline font-semibold" target="_blank">privacy policy</Link>. <span className="text-ei-golddark">*</span>
            </span>
          </label>
          {errors.consent && <p className="text-xs text-red-700 -mt-2 ml-8">{errors.consent}</p>}

          <label className="flex items-start gap-3 cursor-pointer" data-testid="check-marketing">
            <input type="checkbox" className="mt-1 w-5 h-5 accent-[#061A33]" checked={form.marketing_consent} onChange={(e) => set("marketing_consent", e.target.checked)} />
            <span className="text-sm text-ei-graphite/70">
              Optional: I'd like to receive occasional trade updates from EI Battery Solutions.
            </span>
          </label>
        </fieldset>

        <p className="mt-8 text-xs text-ei-graphite/60 leading-relaxed border-l-2 border-ei-gold/60 pl-3">
          Note for review: full sales terms and conditions are not yet supplied. When approved terms are available,
          acceptance will be required here with a recorded version. ABN format is checked but is not verification of
          registration or licensing — eligibility is confirmed by our sales team.
        </p>

        <button type="submit" disabled={submitting} className="mt-8 w-full sm:w-auto font-display text-sm font-bold bg-ei-navy text-ei-white px-8 py-4 rounded-sm hover:bg-ei-ink transition-colors inline-flex items-center justify-center gap-2 min-h-[44px] disabled:opacity-60" data-testid="submit-quote">
          {submitting ? (<><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</>) : "Submit enquiry"}
        </button>
      </form>
    </>
  );
}
